import React, { useState, useEffect } from 'react';
import { 
  Megaphone, LayoutTemplate, Link as LinkIcon, Image as ImageIcon, 
  Upload, Save, Trash2, Edit, X, CheckCircle, AlertCircle, ArrowRight 
} from 'lucide-react';

// Define interface for Ad data
interface Ad {
  id: number;
  title: string;
  description: string;
  section: string;
  type: string;
  contentUrl: string;
  linkUrl: string;
}

const AddAdPage: React.FC = () => {
  // Form States
  const [adTitle, setAdTitle] = useState('');
  const [description, setDescription] = useState('');
  const [section, setSection] = useState('bottom');
  const [adType, setAdType] = useState('image');
  const [adFile, setAdFile] = useState<File | null>(null);
  const [adLink, setAdLink] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  // UI States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adsList, setAdsList] = useState<Ad[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);

  // --- 1. Fetch Ads on Load ---
  const fetchAds = async () => {
    try {
      const res = await fetch('/nodejs/api/ads');
      const data = await res.json();
      setAdsList(data);
    } catch (error) {
      console.error("Error fetching ads:", error);
    }
  };

  useEffect(() => {
    fetchAds();
  }, []);

  // --- 2. Handle File Input ---
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAdFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // --- 3. Handle Submit (Create or Update) ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append('title', adTitle);
    formData.append('description', description);
    formData.append('section', section);
    formData.append('type', adType);
    formData.append('linkUrl', adLink);

    if (adFile) {
      formData.append('adFile', adFile);
    }

    try {
      let url = '/nodejs/api/ads';
      let method = 'POST';

      if (editingId) {
        url = `/nodejs/api/ads/${editingId}`;
        method = 'PUT';
      }

      const response = await fetch(url, {
        method: method,
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to save ad');

      alert(editingId ? 'Ad updated successfully!' : 'Ad created successfully!');
      resetForm();
      fetchAds();
    } catch (error) {
      console.error('Error submitting ad:', error);
      alert('Error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- 4. Handle Delete ---
  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this ad?")) return;

    try {
      await fetch(`/nodejs/api/ads/${id}`, { method: 'DELETE' });
      setAdsList(adsList.filter(ad => ad.id !== id));
    } catch (error) {
      console.error("Error deleting ad:", error);
    }
  };

  // --- 5. Handle Edit Click ---
  const handleEdit = (ad: Ad) => {
    setEditingId(ad.id);
    setAdTitle(ad.title);
    setDescription(ad.description || '');
    setSection(ad.section || 'bottom');
    setAdType(ad.type);
    setAdLink(ad.linkUrl);
    setAdFile(null);
    setPreviewUrl(`/nodejs${ad.contentUrl}`); // Show existing image
    
    // Scroll to top on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setAdTitle('');
    setDescription('');
    setSection('bottom');
    setAdFile(null);
    setAdLink('');
    setPreviewUrl(null);
    setEditingId(null);
  };

  return (
    <div className="min-h-screen bg-white font-sans animate-fade-in-up">
      <div className="max-w-[1600px] mx-auto px-6 py-12">
        
        {/* Header */}
        <div className="mb-12 border-b border-gray-100 pb-6 flex items-center gap-5">
          <div className="w-14 h-14 bg-[#006d5b]/10 rounded-2xl flex items-center justify-center text-[#006d5b] shadow-sm">
             <Megaphone size={28} />
          </div>
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Ad Campaign Manager</h1>
            <p className="text-gray-500 font-medium mt-1 text-sm">Create and manage banners for Sidebar and Footer sections.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* LEFT: EDITOR PANEL (4 Columns) */}
          <div className="lg:col-span-4 space-y-8">
            <div className="bg-white rounded-[30px] border border-gray-200 shadow-xl shadow-gray-100/50 overflow-hidden sticky top-6">
               
               <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                  <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
                    {editingId ? <Edit size={16} className="text-[#006d5b]"/> : <LayoutTemplate size={16} className="text-[#006d5b]"/>}
                    {editingId ? 'Editing Campaign' : 'New Campaign'}
                  </h3>
                  {editingId && (
                    <button onClick={resetForm} className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1">
                      <X size={12} /> Cancel
                    </button>
                  )}
               </div>

               <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  
                  {/* Section Select */}
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block">Placement Zone</label>
                    <div className="grid grid-cols-2 gap-3">
                       <label className={`cursor-pointer border-2 rounded-xl p-3 flex flex-col items-center justify-center gap-2 transition-all ${section === 'sidebar' ? 'border-[#006d5b] bg-[#006d5b]/5 text-[#006d5b]' : 'border-gray-100 text-gray-400 hover:border-gray-200'}`}>
                          <input type="radio" name="section" value="sidebar" checked={section === 'sidebar'} onChange={(e) => setSection(e.target.value)} className="hidden" />
                          <LayoutTemplate size={20} />
                          <span className="text-xs font-bold">Sidebar</span>
                       </label>
                       <label className={`cursor-pointer border-2 rounded-xl p-3 flex flex-col items-center justify-center gap-2 transition-all ${section === 'bottom' ? 'border-[#006d5b] bg-[#006d5b]/5 text-[#006d5b]' : 'border-gray-100 text-gray-400 hover:border-gray-200'}`}>
                          <input type="radio" name="section" value="bottom" checked={section === 'bottom'} onChange={(e) => setSection(e.target.value)} className="hidden" />
                          <ImageIcon size={20} />
                          <span className="text-xs font-bold">Bottom Banner</span>
                       </label>
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block">Campaign Title</label>
                    <input
                      type="text"
                      value={adTitle}
                      onChange={(e) => setAdTitle(e.target.value)}
                      required
                      placeholder="e.g. Summer Sale 50% Off"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:bg-white focus:border-[#006d5b] outline-none transition-all placeholder:text-gray-400"
                    />
                  </div>

                  {/* Target URL */}
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block">Destination Link</label>
                    <div className="relative">
                      <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        value={adLink}
                        onChange={(e) => setAdLink(e.target.value)}
                        required
                        placeholder="https://..."
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-11 pr-4 py-3 text-sm font-bold text-gray-900 focus:bg-white focus:border-[#006d5b] outline-none transition-all placeholder:text-gray-400"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block">Short Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:bg-white focus:border-[#006d5b] outline-none transition-all placeholder:text-gray-400 resize-none"
                      placeholder="Optional details..."
                    />
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block">Creative Asset</label>
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 hover:border-[#006d5b] hover:bg-[#006d5b]/5 transition-all text-center group cursor-pointer relative bg-gray-50/30">
                       <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          required={!editingId}
                       />
                       <div className="flex flex-col items-center justify-center gap-3">
                          {previewUrl ? (
                            <div className="w-full h-32 rounded-lg overflow-hidden relative shadow-sm border border-gray-200">
                               <img src={previewUrl} className="w-full h-full object-cover" alt="Preview" />
                               <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white font-bold text-xs">
                                  Click to Change
                               </div>
                            </div>
                          ) : (
                            <>
                              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-gray-400 group-hover:text-[#006d5b] transition-colors shadow-sm border border-gray-100">
                                <Upload size={20} />
                              </div>
                              <p className="text-xs font-bold text-gray-400">Upload Banner</p>
                            </>
                          )}
                       </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full text-white py-3.5 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2 transition-all active:scale-95 ${
                      editingId 
                        ? 'bg-orange-500 hover:bg-orange-600 shadow-orange-200' 
                        : 'bg-[#006d5b] hover:bg-[#005a4b] shadow-[#006d5b]/20'
                    }`}
                  >
                    {isSubmitting ? 'Processing...' : (editingId ? 'Update Campaign' : 'Publish Campaign')}
                    {!isSubmitting && (editingId ? <CheckCircle size={16}/> : <ArrowRight size={16}/>)}
                  </button>

               </form>
            </div>
          </div>

          {/* RIGHT: GALLERY PANEL (8 Columns) */}
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest">Active Campaigns</h3>
              <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-[10px] font-black uppercase">
                 {adsList.length} Total
              </span>
            </div>

            {adsList.length === 0 ? (
               <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-gray-100 rounded-[30px] bg-gray-50/50">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 text-gray-300 shadow-sm">
                     <ImageIcon size={32} />
                  </div>
                  <h3 className="text-lg font-black text-gray-900">No Ads Live</h3>
                  <p className="text-gray-400 text-sm font-medium mt-1">Create your first ad campaign from the left panel.</p>
               </div>
            ) : (
               <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                 {adsList.map((ad) => (
                   <div key={ad.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl hover:shadow-gray-200/50 hover:border-gray-200 transition-all group relative">
                     
                     {/* Ad Image */}
                     <div className="h-40 w-full bg-gray-100 relative overflow-hidden">
                       <img 
                         src={`http://localhost:2026${ad.contentUrl}`} 
                         alt={ad.title} 
                         className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110"
                         onError={(e) => (e.currentTarget.src = 'https://via.placeholder.com/300?text=No+Image')}
                       />
                       {/* Badge */}
                       <div className="absolute top-3 left-3">
                         <span className={`text-[9px] font-black px-2 py-1 rounded-lg text-white uppercase tracking-wider shadow-sm ${
                           ad.section === 'sidebar' ? 'bg-purple-600' : 'bg-emerald-600'
                         }`}>
                           {ad.section}
                         </span>
                       </div>
                     </div>
                     
                     {/* Content */}
                     <div className="p-5">
                       <h3 className="font-black text-gray-900 text-base truncate mb-1">{ad.title}</h3>
                       <div className="flex items-center gap-1 text-xs font-bold text-gray-400 truncate mb-4">
                         <LinkIcon size={12} />
                         <span className="truncate">{ad.linkUrl}</span>
                       </div>
                       
                       {/* Action Overlay (Always visible on mobile, hover on desktop) */}
                       <div className="flex gap-2 pt-4 border-t border-gray-50">
                         <button 
                           onClick={() => handleEdit(ad)}
                           className="flex-1 bg-gray-50 hover:bg-[#006d5b] hover:text-white text-gray-500 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                         >
                           <Edit size={14} /> Edit
                         </button>
                         <button 
                           onClick={() => handleDelete(ad.id)}
                           className="flex-1 bg-red-50 hover:bg-red-500 hover:text-white text-red-500 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                         >
                           <Trash2 size={14} /> Delete
                         </button>
                       </div>
                     </div>
                   </div>
                 ))}
               </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default AddAdPage;